<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Account Leads</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Add Account Lead
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear v-if="store.loading" indeterminate></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="store.error" type="error" class="mb-4">
      {{ store.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!store.loading && store.leads.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-account-arrow-right-outline</v-icon
      >
      <h3 class="text-h6 mb-2">No Account Leads</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Get started by creating your first account lead.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Add Account Lead
      </v-btn>
    </v-card>

    <!-- Account Leads List -->
    <v-card v-if="!store.loading && store.leads.length > 0">
      <v-list>
        <v-list-item
          v-for="lead in store.leads"
          :key="lead.id"
          class="border-b"
        >
          <template #prepend>
            <v-icon color="primary">mdi-account-arrow-right</v-icon>
          </template>

          <v-list-item-title>{{ lead.name }}</v-list-item-title>

          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="openEditDialog(lead)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="confirmDelete(lead)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Edit Account Lead" : "Add Account Lead" }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Name"
              :rules="nameRules"
              required
              autofocus
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
          >
            {{ isEditing ? "Update" : "Create" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ leadToDelete?.name }}"? This
          action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteLead">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useAccountLeadsStoreRefactor } from "@/stores/accountLeadsStoreRefactor";

const store = useAccountLeadsStoreRefactor();

// Data
const dialog = ref(false);
const deleteDialog = ref(false);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const leadToDelete = ref(null);

const formData = reactive({
  id: null,
  name: "",
});

// Form validation rules
const nameRules = [
  (v) => !!v || "Name is required",
  (v) => (v && v.length <= 255) || "Name must be less than 255 characters",
];

// Methods
const openCreateDialog = () => {
  isEditing.value = false;
  formData.id = null;
  formData.name = "";
  dialog.value = true;
};

const openEditDialog = (lead) => {
  isEditing.value = true;
  formData.id = lead.id;
  formData.name = lead.name;
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  if (formData.form) {
    formData.form.reset();
  }
};

const save = async () => {
  if (!formValid.value) return;

  saving.value = true;

  try {
    const leadData = {
      name: formData.name,
    };

    if (isEditing.value) {
      // Include the ID in the data object for update
      leadData.id = formData.id;
      await store.update(leadData);
    } else {
      await store.create(leadData);
    }

    closeDialog();
  } catch (error) {
    console.error("Error saving lead:", error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (lead) => {
  leadToDelete.value = lead;
  deleteDialog.value = true;
};

const deleteLead = async () => {
  if (!leadToDelete.value) return;

  deleting.value = true;

  try {
    await store.destroy(leadToDelete.value.id);
    deleteDialog.value = false;
    leadToDelete.value = null;
  } catch (error) {
    console.error("Error deleting lead:", error);
  } finally {
    deleting.value = false;
  }
};

// Load data on mount
onMounted(async () => {
  await store.fetchAll();
});
</script>
